/**
 * 智能回复：会话列表 / 消息 / 店铺轮询状态
 */
import Call from "@/script/call";
import {
    playKefuNotifySound,
    explainDingForMessages,
} from "@/utils/kefuNotifySound";
import {
    isKefuShopAuthFail,
    notifyKefuShopAuthFail,
    clearKefuShopAuthFailCooldown,
} from "@/utils/kefuShopAuthAlert";

const REPLY_SLA_SEC = 160;
/** 会话列表：不按单店筛选时用（店铺栏「全部」） */
const SHOP_FILTER_ALL = "__all__";
function soundStoreLog(step, extra) {
    try {
        const line =
            `[sound] ${step} ` +
            JSON.stringify(Object.assign({ at: Date.now() }, extra || {}));
        Call.sendSync("fileEvent.log", "kefu-smart-reply.txt", line);
    } catch (e) {}
}

/** 合并咨询宝贝：空 title/img/price 不冲掉已有展示字段 */
function mergeLastConsultProduct(prev, next, at) {
    const p = prev && typeof prev === "object" ? prev : {};
    const n = next && typeof next === "object" ? next : null;
    if (!n) return Object.keys(p).length ? p : null;
    if (p.productId && n.productId && String(p.productId) !== String(n.productId)) {
        return { ...n, at: at != null ? at : n.at || Date.now() };
    }
    const merged = { ...p, ...n, at: at != null ? at : n.at || p.at || Date.now() };
    if (!n.title && p.title) merged.title = p.title;
    if (!n.img && p.img) merged.img = p.img;
    if (!n.price && p.price) merged.price = p.price;
    if (
        (n.sellNum == null || n.sellNum === "") &&
        p.sellNum != null &&
        p.sellNum !== ""
    ) {
        merged.sellNum = p.sellNum;
    }
    if (!n.channelParams && p.channelParams) merged.channelParams = p.channelParams;
    if (!n.couponPrice && p.couponPrice) merged.couponPrice = p.couponPrice;
    if (!n.guaranteeText && p.guaranteeText) merged.guaranteeText = p.guaranteeText;
    if (
        !(n.activityLabels && n.activityLabels.length) &&
        p.activityLabels &&
        p.activityLabels.length
    ) {
        merged.activityLabels = p.activityLabels;
    }
    if (!n.logisticsText && p.logisticsText) merged.logisticsText = p.logisticsText;
    if (!n.logistics && p.logistics) merged.logistics = p.logistics;
    return merged;
}

const state = {
    shops: [],
    /** shopId -> 是否开启智能自动回复 */
    smartReplyEnabledByShop: {},
    /** shopId -> 是否开启 AI 客服（依赖智能回复） */
    aiReplyEnabledByShop: {},
    /** shopId -> convId -> conversation */
    conversationsByShop: {},
    /** msgId set for dedupe: shopId:msgId */
    messageIdSet: {},
    /** shopId -> convId -> messages[] */
    messagesByConv: {},
    activeShopId: "",
    /** 会话列表筛选：SHOP_FILTER_ALL=全部店铺，否则为单店 id */
    shopFilterId: SHOP_FILTER_ALL,
    activeConvId: "",
    replySlaSec: REPLY_SLA_SEC,
    /** 此时间之前的买家消息不计入店铺红点 */
    badgeClearedAt: 0,
    started: false,
    /** worker 后台补全售后：{ shopId, conversationId, orders, at } */
    orderAfterSalePatch: null,
};

const mutations = {
    CLEAR_WAITING_BADGES(state, at) {
        const cut = Number(at) || Date.now();
        if (cut > (state.badgeClearedAt || 0)) state.badgeClearedAt = cut;
        const byShop = state.conversationsByShop || {};
        const next = {};
        Object.keys(byShop).forEach((shopId) => {
            const bucket = { ...(byShop[shopId] || {}) };
            Object.keys(bucket).forEach((cid) => {
                const c = bucket[cid];
                if (!c) return;
                bucket[cid] = { ...c, waitingReply: false };
            });
            next[shopId] = bucket;
        });
        state.conversationsByShop = next;
    },
    SET_ORDER_AFTERSALE_PATCH(state, payload) {
        state.orderAfterSalePatch = payload
            ? {
                  shopId: String(payload.shopId || ""),
                  conversationId: String(payload.conversationId || ""),
                  orders: Array.isArray(payload.orders) ? payload.orders : [],
                  at: payload.at || Date.now(),
              }
            : null;
    },
    RESET(state) {
        state.shops = [];
        state.smartReplyEnabledByShop = {};
        state.aiReplyEnabledByShop = {};
        state.conversationsByShop = {};
        state.orderAfterSalePatch = null;
        state.messageIdSet = {};
        state.messagesByConv = {};
        state.activeShopId = "";
        state.activeConvId = "";
        state.started = false;
    },
    SET_SMART_REPLY_ENABLED(state, payload) {
        if (!payload || payload.shopId == null) return;
        const shopId = String(payload.shopId);
        state.smartReplyEnabledByShop = {
            ...state.smartReplyEnabledByShop,
            [shopId]: !!payload.enabled,
        };
    },
    SET_SMART_REPLY_ENABLED_MAP(state, map) {
        const next = { ...state.smartReplyEnabledByShop };
        const src = map || {};
        Object.keys(src).forEach((k) => {
            next[String(k)] = !!src[k];
        });
        state.smartReplyEnabledByShop = next;
    },
    SET_AI_REPLY_ENABLED(state, payload) {
        if (!payload || payload.shopId == null) return;
        const shopId = String(payload.shopId);
        state.aiReplyEnabledByShop = {
            ...state.aiReplyEnabledByShop,
            [shopId]: !!payload.enabled,
        };
    },
    SET_AI_REPLY_ENABLED_MAP(state, map) {
        const next = { ...state.aiReplyEnabledByShop };
        const src = map || {};
        Object.keys(src).forEach((k) => {
            next[String(k)] = !!src[k];
        });
        state.aiReplyEnabledByShop = next;
    },
    SET_STARTED(state, v) {
        state.started = !!v;
    },
    SET_ACTIVE_SHOP(state, shopId) {
        state.activeShopId = shopId ? String(shopId) : "";
    },
    SET_SHOP_FILTER(state, shopId) {
        const id = shopId != null ? String(shopId) : "";
        state.shopFilterId = id || SHOP_FILTER_ALL;
    },
    SET_ACTIVE_CONV(state, convId) {
        state.activeConvId = convId ? String(convId) : "";
    },
    APPLY_SNAPSHOT(state, data) {
        if (!data) return;
        if (data.replySlaSec) state.replySlaSec = data.replySlaSec;
        if (Number(data.badgeClearedAt) > (state.badgeClearedAt || 0)) {
            state.badgeClearedAt = Number(data.badgeClearedAt);
        }
        const shops = data.shops || [];
        state.shops = shops.map((s) => ({ ...s }));
        const enabledMap = { ...state.smartReplyEnabledByShop };
        const aiMap = { ...state.aiReplyEnabledByShop };
        shops.forEach((s) => {
            if (!s || s.shopId == null) return;
            if (s.smartReplyEnabled != null) {
                enabledMap[String(s.shopId)] = !!s.smartReplyEnabled;
            }
            if (s.aiReplyEnabled != null) {
                aiMap[String(s.shopId)] = !!s.aiReplyEnabled;
            }
        });
        state.smartReplyEnabledByShop = enabledMap;
        state.aiReplyEnabledByShop = aiMap;
        const byShop = { ...state.conversationsByShop };
        const convMap = data.conversations || {};
        Object.keys(convMap).forEach((shopId) => {
            const list = convMap[shopId] || [];
            const bucket = { ...(byShop[shopId] || {}) };
            list.forEach((c) => {
                if (!c || !c.securityConversationId) return;
                const id = c.securityConversationId;
                bucket[id] = { ...(bucket[id] || {}), ...c, id };
            });
            byShop[shopId] = bucket;
        });
        state.conversationsByShop = byShop;
    },
    UPSERT_SHOP_STATUS(state, payload) {
        if (!payload || !payload.shopId) return;
        const shopId = String(payload.shopId);
        const idx = state.shops.findIndex((s) => String(s.shopId) === shopId);
        const row = {
            shopId,
            shopName: payload.shopName || (idx >= 0 ? state.shops[idx].shopName : ""),
            platformShopId:
                payload.platformShopId ||
                (idx >= 0 ? state.shops[idx].platformShopId : ""),
            status: payload.status || "idle",
            errorMsg: payload.errorMsg || "",
            polling: payload.polling != null ? payload.polling : true,
        };
        if (idx >= 0) {
            const next = state.shops.slice();
            next[idx] = { ...next[idx], ...row };
            state.shops = next;
        } else {
            state.shops = state.shops.concat([row]);
        }
    },
    MERGE_CONV(state, payload) {
        if (!payload) return;
        const shopId = String(payload.shopId || "");
        const list = payload.conversations || [];
        if (!shopId || !list.length) return;
        const bucket = { ...(state.conversationsByShop[shopId] || {}) };
        list.forEach((c) => {
            if (!c || !c.securityConversationId) return;
            const id = c.securityConversationId;
            const prev = bucket[id] || {};
            const next = {
                ...prev,
                ...c,
                id,
                shopId: shopId || prev.shopId || c.shopId || "",
            };
            // worker 常推空 title/img 的咨询宝贝，勿冲掉前端已补全的图
            if (Object.prototype.hasOwnProperty.call(c, "lastConsultProduct")) {
                if (c.lastConsultProduct == null) {
                    next.lastConsultProduct = null;
                } else {
                    next.lastConsultProduct = mergeLastConsultProduct(
                        prev.lastConsultProduct,
                        c.lastConsultProduct
                    );
                }
            }
            bucket[id] = next;
        });
        state.conversationsByShop = {
            ...state.conversationsByShop,
            [shopId]: bucket,
        };
    },
    APPEND_MESSAGES(state, payload) {
        if (!payload) return;
        const shopId = String(payload.shopId || "");
        const messages = payload.messages || [];
        if (!shopId || !messages.length) return;

        const idSet = { ...state.messageIdSet };
        const msgMap = { ...state.messagesByConv };
        const bucket = { ...(state.conversationsByShop[shopId] || {}) };

        messages.forEach((m) => {
            if (!m || !m.securityConversationId) return;
            const cid = m.securityConversationId;
            const mid = `${shopId}:${m.id}`;
            const key = `${shopId}::${cid}`;
            if (!idSet[mid]) {
                idSet[mid] = 1;
                const arr = (msgMap[key] || []).slice();
                // 真实预填售后卡：去掉同会话本地乐观卡
                if (
                    m.orderCard &&
                    m.orderCard.kind === "pre_aftersale" &&
                    m.orderCard.cardId &&
                    !String(m.id).startsWith("local_pre_aftersale_")
                ) {
                    const oid = String(m.orderCard.orderId || "");
                    for (let i = arr.length - 1; i >= 0; i--) {
                        const old = arr[i];
                        if (
                            old &&
                            String(old.id || "").startsWith("local_pre_aftersale_") &&
                            old.orderCard &&
                            old.orderCard.kind === "pre_aftersale" &&
                            (!oid || String(old.orderCard.orderId || "") === oid)
                        ) {
                            delete idSet[`${shopId}:${old.id}`];
                            arr.splice(i, 1);
                        }
                    }
                }
                arr.push(m);
                arr.sort((a, b) => (a.createTime || 0) - (b.createTime || 0));
                if (arr.length > 800) arr.splice(0, arr.length - 800);
                msgMap[key] = arr;
            } else if (m.orderCard || (m.productCard && m.productCard.productId)) {
                // 已存在消息：补全/更新订单卡或商品卡
                const arr = (msgMap[key] || []).slice();
                const idx = arr.findIndex((x) => x && String(x.id) === String(m.id));
                if (idx >= 0) {
                    const old = arr[idx] || {};
                    if (m.orderCard) {
                        const oldCard = old.orderCard || {};
                        const nextCard = m.orderCard || {};
                        arr[idx] = {
                            ...old,
                            ...m,
                            orderCard: { ...oldCard, ...nextCard },
                            // 订单/预填售后卡优先：清掉误解析的商品卡
                            productCard:
                                m.productCard != null ? m.productCard : null,
                            preview: m.preview || old.preview,
                            msgType: m.msgType || old.msgType,
                        };
                        msgMap[key] = arr;
                    } else if (m.productCard && m.productCard.productId) {
                        const oldCard = old.productCard || {};
                        const nextCard = m.productCard || {};
                        const richer =
                            (!oldCard.title && nextCard.title) ||
                            (!oldCard.img && nextCard.img) ||
                            (!oldCard.price && nextCard.price);
                        if (richer || !old.productCard) {
                            arr[idx] = {
                                ...old,
                                ...m,
                                productCard: { ...oldCard, ...nextCard },
                                preview: m.preview || old.preview,
                                msgType: m.msgType || old.msgType,
                            };
                            msgMap[key] = arr;
                        }
                    }
                }
            } else if (m.imageCard && m.imageCard.url) {
                // 已存在消息：补全图片卡（本地 dataURL → CDN https）
                const arr = (msgMap[key] || []).slice();
                const idx = arr.findIndex((x) => x && String(x.id) === String(m.id));
                if (idx >= 0) {
                    const old = arr[idx] || {};
                    const oldCard = old.imageCard || {};
                    const oldUrl = String(oldCard.url || "");
                    const nextUrl = String(m.imageCard.url || "");
                    // 本地 dataURL 预览立刻可显；CDN 刚上传可能未就绪，勿用 https 覆盖以免二次裂图
                    if (oldUrl.indexOf("data:image/") === 0) {
                        // 仅同步其它字段，保留本地预览
                        arr[idx] = {
                            ...old,
                            ...m,
                            imageCard: {
                                ...oldCard,
                                ...m.imageCard,
                                url: oldUrl,
                                cdnUrl: /^https?:\/\//i.test(nextUrl)
                                    ? nextUrl
                                    : oldCard.cdnUrl || "",
                            },
                            msgType: m.msgType || old.msgType || "file_image",
                            preview: m.preview || old.preview || "[图片]",
                        };
                        msgMap[key] = arr;
                    } else {
                        const fillMissing = !oldUrl && !!nextUrl;
                        const replaceHttp =
                            /^https?:\/\//i.test(oldUrl) &&
                            /^https?:\/\//i.test(nextUrl) &&
                            oldUrl !== nextUrl;
                        if (fillMissing || replaceHttp) {
                            arr[idx] = {
                                ...old,
                                ...m,
                                imageCard: { ...oldCard, ...m.imageCard },
                                msgType: m.msgType || old.msgType || "file_image",
                                preview: m.preview || old.preview || "[图片]",
                            };
                            msgMap[key] = arr;
                        }
                    }
                }
            }

            let conv = bucket[cid] || {
                id: cid,
                shopId,
                securityConversationId: cid,
                conversationShortId: m.conversationShortId,
                subConversationShortId: m.subConversationShortId,
                buyerName: m.buyerName,
                buyerAvatar: m.buyerAvatar,
                buyerSecurityId: m.buyerSecurityId,
                lastContent: "",
                lastMsgTime: 0,
                lastBuyerMsgTime: 0,
                waitingReply: false,
                replySlaSec: state.replySlaSec,
            };
            if (m.conversationShortId) conv.conversationShortId = m.conversationShortId;
            if (m.subConversationShortId) {
                conv.subConversationShortId = m.subConversationShortId;
            }
            if (m.buyerName && m.buyerName !== "客户") conv.buyerName = m.buyerName;
            if (m.buyerAvatar) conv.buyerAvatar = m.buyerAvatar;
            if (m.buyerSecurityId) conv.buyerSecurityId = m.buyerSecurityId;
            if (m.productCard && m.productCard.productId) {
                const prev = conv.lastConsultProduct || {};
                const next = m.productCard;
                const preferNext =
                    !prev.productId ||
                    String(prev.productId) === String(next.productId);
                if (preferNext) {
                    conv.lastConsultProduct = mergeLastConsultProduct(
                        prev,
                        next,
                        m.createTime || prev.at || Date.now()
                    );
                }
            }
            if ((m.createTime || 0) >= (conv.lastMsgTime || 0)) {
                conv.lastMsgTime = m.createTime;
                conv.lastContent = m.preview || m.content || "";
            }
            const buyerText = m.content || m.preview || "";
            const isClosed = /系统关闭会话|超时未回复/.test(buyerText);
            const isNoise =
                /^[\[【].*[\]】]$/.test(String(buyerText).trim()) ||
                /平台可能主动介入|欢迎光临|请问有什么可以帮|很高兴为您服务|\[invite_comment\]|用户正在查看|用户正在浏览/.test(
                    buyerText
                );
            if (m.fromBuyer) {
                if (isClosed) {
                    if ((m.createTime || 0) >= (conv.lastBuyerMsgTime || 0)) {
                        conv.waitingReply = false;
                    }
                } else if (!isNoise) {
                    if ((m.createTime || 0) >= (conv.lastBuyerMsgTime || 0)) {
                        conv.lastBuyerMsgTime = m.createTime;
                        if ((m.createTime || 0) > (state.badgeClearedAt || 0)) {
                            conv.waitingReply = true;
                        }
                    }
                }
            } else if ((m.createTime || 0) >= (conv.lastBuyerMsgTime || 0)) {
                conv.waitingReply = false;
            }
            bucket[cid] = { ...conv, id: cid, shopId };
        });

        state.messageIdSet = idSet;
        state.messagesByConv = msgMap;
        state.conversationsByShop = {
            ...state.conversationsByShop,
            [shopId]: bucket,
        };
    },
    /** 用补全后的商品卡更新会话消息 / 咨询宝贝 */
    PATCH_PRODUCT_CARD(state, payload) {
        if (!payload) return;
        const shopId = String(payload.shopId || "");
        const convId = String(payload.convId || "");
        const msgId = payload.msgId != null ? String(payload.msgId) : "";
        const card = payload.productCard;
        if (!shopId || !convId || !card || !card.productId) return;
        const pid = String(card.productId);

        const key = `${shopId}::${convId}`;
        const arr = (state.messagesByConv[key] || []).slice();
        let changed = false;
        for (let i = 0; i < arr.length; i++) {
            const m = arr[i];
            if (!m) continue;
            const byId = msgId && String(m.id) === msgId;
            const byCard =
                m.productCard && String(m.productCard.productId) === pid;
            const byPreview = String(m.preview || m.content || "").indexOf(pid) >= 0;
            if (!byId && !byCard && !byPreview) continue;
            arr[i] = {
                ...m,
                productCard: { ...(m.productCard || {}), ...card },
                preview: card.title ? `[商品] ${card.title}` : m.preview,
            };
            changed = true;
            if (byId) break;
        }
        if (changed) {
            state.messagesByConv = { ...state.messagesByConv, [key]: arr };
        }

        const bucket = { ...(state.conversationsByShop[shopId] || {}) };
        const conv = bucket[convId];
        if (!conv) return;
        const prev = conv.lastConsultProduct || {};
        if (prev.productId && String(prev.productId) !== pid) return;
        const merged = mergeLastConsultProduct(prev, card, Date.now());
        bucket[convId] = {
            ...conv,
            lastConsultProduct: merged,
            lastContent:
                merged.title && String(conv.lastContent || "").indexOf("[商品]") === 0
                    ? `[商品] ${merged.title}`
                    : conv.lastContent,
        };
        state.conversationsByShop = {
            ...state.conversationsByShop,
            [shopId]: bucket,
        };
    },
    /** 补全/更新消息上的订单业务卡（物流卡轨迹等） */
    PATCH_ORDER_CARD(state, payload) {
        if (!payload) return;
        const shopId = String(payload.shopId || "");
        const convId = String(payload.convId || "");
        const msgId = payload.msgId != null ? String(payload.msgId) : "";
        const card = payload.orderCard;
        if (!shopId || !convId || !card) return;
        const key = `${shopId}::${convId}`;
        const arr = (state.messagesByConv[key] || []).slice();
        let changed = false;
        for (let i = 0; i < arr.length; i++) {
            const m = arr[i];
            if (!m) continue;
            const byId = msgId && String(m.id) === msgId;
            const byKind =
                m.orderCard &&
                card.kind &&
                String(m.orderCard.kind || "") === String(card.kind) &&
                (!card.orderId ||
                    String(m.orderCard.orderId || "") === String(card.orderId));
            if (!byId && !(msgId === "" && byKind)) continue;
            arr[i] = {
                ...m,
                orderCard: { ...(m.orderCard || {}), ...card },
                productCard: null,
            };
            changed = true;
            if (byId) break;
        }
        if (changed) {
            state.messagesByConv = { ...state.messagesByConv, [key]: arr };
        }
    },
};

const getters = {
    activeShop(state) {
        return state.shops.find((s) => String(s.shopId) === String(state.activeShopId)) || null;
    },
    conversationList(state) {
        const filterId = String(state.shopFilterId || SHOP_FILTER_ALL);
        const byShop = state.conversationsByShop || {};
        const nameById = {};
        (state.shops || []).forEach((s) => {
            if (!s || s.shopId == null) return;
            nameById[String(s.shopId)] = s.shopName || s.shop_name || "";
        });
        const stamp = (shopId, c) => {
            if (!c) return null;
            const sid = String(c.shopId || shopId || "");
            return {
                ...c,
                shopId: sid,
                shopName: c.shopName || nameById[sid] || "",
                id: c.id || c.securityConversationId,
            };
        };
        let list = [];
        if (!filterId || filterId === SHOP_FILTER_ALL) {
            Object.keys(byShop).forEach((shopId) => {
                const bucket = byShop[shopId] || {};
                Object.keys(bucket).forEach((cid) => {
                    const row = stamp(shopId, bucket[cid]);
                    if (row) list.push(row);
                });
            });
        } else {
            const bucket = byShop[filterId] || {};
            list = Object.keys(bucket)
                .map((cid) => stamp(filterId, bucket[cid]))
                .filter(Boolean);
        }
        return list.sort(
            (a, b) => (b.lastMsgTime || 0) - (a.lastMsgTime || 0)
        );
    },
    isShopFilterAll(state) {
        const id = String(state.shopFilterId || SHOP_FILTER_ALL);
        return !id || id === SHOP_FILTER_ALL;
    },
    activeConversation(state) {
        const shopId = state.activeShopId;
        const convId = state.activeConvId;
        if (!shopId || !convId) return null;
        const bucket = state.conversationsByShop[shopId] || {};
        return bucket[convId] || null;
    },
    activeMessages(state) {
        const shopId = state.activeShopId;
        const convId = state.activeConvId;
        if (!shopId || !convId) return [];
        return state.messagesByConv[`${shopId}::${convId}`] || [];
    },
    isSmartReplyEnabled: (state) => (shopId) => {
        return !!state.smartReplyEnabledByShop[String(shopId)];
    },
    /** 各店待回复会话数（买家最新消息尚未回复；排除已系统关闭） */
    waitingReplyCountByShop(state) {
        const map = {};
        const byShop = state.conversationsByShop || {};
        Object.keys(byShop).forEach((shopId) => {
            const bucket = byShop[shopId] || {};
            let n = 0;
            Object.keys(bucket).forEach((cid) => {
                const c = bucket[cid];
                if (!c || !c.waitingReply) return;
                const last = `${c.lastContent || ""}`;
                if (/系统关闭会话|超时未回复/.test(last)) return;
                if (/^[\[【].*[\]】]$/.test(last.trim())) return;
                n += 1;
            });
            if (n > 0) map[shopId] = n;
        });
        return map;
    },
    /** 各店最近买家消息时间（用于有新消息的店顶到前面） */
    lastBuyerMsgTimeByShop(state) {
        const map = {};
        const byShop = state.conversationsByShop || {};
        Object.keys(byShop).forEach((shopId) => {
            const bucket = byShop[shopId] || {};
            let maxT = 0;
            Object.keys(bucket).forEach((cid) => {
                const c = bucket[cid];
                const t = Number((c && (c.lastBuyerMsgTime || c.lastMsgTime)) || 0);
                if (t > maxT) maxT = t;
            });
            if (maxT > 0) map[shopId] = maxT;
        });
        return map;
    },
};

const actions = {
    onWorkerUpdate({ commit, state }, payload) {
        if (!payload || !payload.type) return;
        if (payload.type === "clear-waiting-badges") {
            commit("CLEAR_WAITING_BADGES", payload.clearedAt);
            return;
        }
        if (payload.type === "shop-status") {
            const shopId = String(payload.shopId || "");
            const prev = (state.shops || []).find(
                (s) => String(s.shopId) === shopId
            );
            const wasAuth =
                prev && isKefuShopAuthFail(prev.status, prev.errorMsg);
            const nowAuth = isKefuShopAuthFail(
                payload.status,
                payload.errorMsg
            );
            commit("UPSERT_SHOP_STATUS", payload);
            if (nowAuth && !wasAuth) {
                const shopName =
                    payload.shopName ||
                    (prev && prev.shopName) ||
                    "";
                try {
                    notifyKefuShopAuthFail({
                        shopId,
                        shopName,
                        status: payload.status,
                        errorMsg: payload.errorMsg,
                    });
                } catch (e) {}
            } else if (
                String(payload.status || "") === "online" ||
                (prev &&
                    wasAuth &&
                    !nowAuth &&
                    String(payload.status || "") !== "error")
            ) {
                try {
                    clearKefuShopAuthFailCooldown(shopId);
                } catch (e) {}
            }
            return;
        }
        if (payload.type === "shop-smart-reply") {
            commit("SET_SMART_REPLY_ENABLED", {
                shopId: payload.shopId,
                enabled: payload.enabled,
            });
            return;
        }
        if (payload.type === "shop-ai-reply") {
            commit("SET_AI_REPLY_ENABLED", {
                shopId: payload.shopId,
                enabled: payload.enabled,
            });
            return;
        }
        if (payload.type === "orderAfterSale") {
            commit("SET_ORDER_AFTERSALE_PATCH", payload);
            return;
        }
        if (payload.type === "messages") {
            let ding = false;
            let ex = { ding: false, reason: "init" };
            try {
                // 必须在 APPEND 前判断：APPEND 会写入 messageIdSet，否则永远判成 dup 不响
                ex = explainDingForMessages(state, payload) || ex;
                ding = !!ex.ding;
                const msgN = (payload.messages && payload.messages.length) || 0;
                // 小批量（更像实时新消息）必打点；大批量对账只抽稀
                if (msgN > 0 && (msgN <= 30 || ding || ex.reason === "stale_or_system")) {
                    soundStoreLog(ding ? "ding.yes" : "ding.skip", {
                        shopId: String(payload.shopId || ""),
                        msgN,
                        reason: ex.reason,
                        buyerN: ex.buyerN || 0,
                        newN: ex.newN || 0,
                        freshN: ex.freshN || 0,
                    });
                }
            } catch (e) {
                soundStoreLog("ding.err", {
                    msg: (e && e.message) || String(e),
                });
            }
            if (payload.conversations && payload.conversations.length) {
                commit("MERGE_CONV", {
                    shopId: payload.shopId,
                    conversations: payload.conversations,
                });
            }
            commit("APPEND_MESSAGES", payload);
            // 点开轻量会话后 worker 可能纠正 cid，切到真会话以免「暂无消息」
            const focusId = String(payload.focusConversationId || "").trim();
            if (
                focusId &&
                payload.shopId &&
                String(state.activeShopId || "") === String(payload.shopId) &&
                String(state.activeConvId || "") !== focusId
            ) {
                commit("SET_ACTIVE_CONV", focusId);
            }
            if (ding) {
                try {
                    playKefuNotifySound();
                } catch (e) {
                    soundStoreLog("play.throw", {
                        msg: (e && e.message) || String(e),
                    });
                }
            }
        }
    },
};

export { SHOP_FILTER_ALL };

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};
